import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { TrendingUp, Users, CreditCard, DollarSign, BarChart3, Calendar as CalendarIcon } from "lucide-react";
import { useAppData } from "../hooks/useAppData";
import { useCuotasLote } from "../hooks/useCuotasLote";
import { CuotasPagosCard } from "./ui/CuotasPagosCard";
import { PinInput } from "./ui/PinInput";
import { NumericKeypad } from "./ui/NumericKeypad";

interface SocioDashboardProps {
  totalLotes: number;
  stats: { disponibles: number; credito: number; vendidos: number };
  financial: {
    totalIngresos: number;
    totalEgresos: number;
    utilidadBruta: number;
    proyeccionAnual: number;
    promedioMensual: number;
    mejorMes: string;
    eficiencia: number;
    flujoCajaProyectado?: number; // Added for the new card
  };
}

export default function SocioDashboard(props: any) {
  console.log('SocioDashboard props:', props);
  const { usuarios } = useAppData();
  const [nombre, setNombre] = React.useState(props.nombre || "");
  const [pin, setPin] = React.useState(props.pin || "");
  const [autenticado, setAutenticado] = React.useState(props.autenticado || false);
  const [intentosFallidos, setIntentosFallidos] = React.useState(0);
  const [bloqueado, setBloqueado] = React.useState(false);
  const [tiempoBloqueo, setTiempoBloqueo] = React.useState(0);
  const [step, setStep] = React.useState<'nombre' | 'pin'>('nombre');
  const [errorNombre, setErrorNombre] = React.useState("");
  const [showLotesCredito, setShowLotesCredito] = React.useState(false);
  const [showLotesContado, setShowLotesContado] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [showInfoFinancieraScreen, setShowInfoFinancieraScreen] = React.useState(false);

  // Buscar socio por nombre
  const socio = React.useMemo(() => {
    if (!nombre) return null;
    return usuarios.find((u: any) => u.tipo === 'socio' && u.nombre === nombre);
  }, [usuarios, nombre]);

  // Buscar socio autenticado (nombre + pin)
  const socioAutenticado = React.useMemo(() => {
    if (!nombre || !pin) return null;
    return usuarios.find((u: any) => u.tipo === 'socio' && u.nombre === nombre && u.pin === pin);
  }, [usuarios, nombre, pin]);

  React.useEffect(() => {
    if (socioAutenticado) {
      setAutenticado(true);
      setIntentosFallidos(0);
    } else if (step === 'pin' && pin.length === 4 && usuarios.length > 0) {
      setAutenticado(false);
      setIntentosFallidos(prev => prev + 1);
      setPin("");
      if (intentosFallidos >= 4) {
        setBloqueado(true);
        setTiempoBloqueo(30);
      }
    }
  }, [socioAutenticado, usuarios.length, pin, intentosFallidos, step]);

  React.useEffect(() => {
    if (bloqueado && tiempoBloqueo > 0) {
      const timer = setTimeout(() => {
        setTiempoBloqueo(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (bloqueado && tiempoBloqueo === 0) {
      setBloqueado(false);
      setIntentosFallidos(0);
      setPin("");
    }
  }, [bloqueado, tiempoBloqueo]);

  const handleNombreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!socio) {
      setErrorNombre("Nombre de socio no válido");
      return;
    }
    setErrorNombre("");
    setStep('pin');
  };

  const handlePinComplete = (val: string) => {
    if (val.length === 4) {
      setPin(val);
    }
  };

  if (bloqueado) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-medium text-red-500 mb-2">Acceso Bloqueado</h2>
          <p className="text-muted-foreground mb-4">Demasiados intentos fallidos. Intenta de nuevo en:</p>
          <div className="text-3xl font-bold text-blue-500 mb-4">
            {Math.floor(tiempoBloqueo / 60)}:{(tiempoBloqueo % 60).toString().padStart(2, '0')}
          </div>
          <p className="text-sm text-muted-foreground">Por seguridad, tu cuenta ha sido bloqueada temporalmente.</p>
        </div>
      </div>
    );
  }

  if (!autenticado) {
    if (step === 'nombre') {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center px-4 pt-20">
          <div className="w-full max-w-md mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Bimobi</h2>
            <h3 className="text-xl font-medium text-muted-foreground mb-1">Acceso de Socio</h3>
            <p className="text-muted-foreground">Socio del proyecto</p>
          </div>
          <form className="w-full max-w-md mb-6" onSubmit={handleNombreSubmit}>
            <label className="block mb-2 text-sm font-medium text-muted-foreground">Nombre de usuario</label>
            <input
              type="text"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-input bg-secondary text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
              placeholder="Nombre de socio"
              autoComplete="username"
            />
            {errorNombre && <div className="mt-2 text-red-500 text-sm">{errorNombre}</div>}
            <button
              type="submit"
              className={`w-full mt-6 py-3 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring
                ${nombre ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
              disabled={!nombre}
            >
              Siguiente
            </button>
          </form>
        </div>
      );
    } else if (step === 'pin') {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center px-4 pt-20">
          <div className="w-full max-w-md mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Bimobi</h2>
            <h3 className="text-xl font-medium text-muted-foreground mb-1">Acceso de Socio</h3>
            <p className="text-muted-foreground">Socio del proyecto</p>
          </div>
          <div className="w-full max-w-md mb-6 flex justify-between items-center">
            <span className="block mb-2 text-sm font-medium text-muted-foreground">PIN</span>
            <button
              className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
              onClick={() => { setStep('nombre'); setPin(""); setIntentosFallidos(0); }}
              type="button"
            >
              Salir
            </button>
          </div>
          <PinInput value={pin} onChange={setPin} length={4} />
          <NumericKeypad value={pin} length={4} onChange={setPin} />
          <button
            className={`w-full max-w-md mt-8 py-3 rounded-lg font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring
              ${pin.length === 4 ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
            disabled={pin.length !== 4}
            onClick={() => handlePinComplete(pin)}
          >
            Ingresar
          </button>
          {intentosFallidos > 0 && (
            <div className="w-full max-w-md mt-4 text-center text-red-500">
              PIN incorrecto. Intentos restantes: {5 - intentosFallidos}
            </div>
          )}
        </div>
      );
    }
  }

  // Si autenticado, mostrar dashboard original
  if (showLotesContado) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md flex justify-between items-center mb-8">
          <h2 className="text-xl font-semibold text-left text-foreground">Lotes de Contado</h2>
          <button
            className="px-3 py-1.5 rounded-md bg-secondary hover:bg-accent text-muted-foreground hover:text-foreground text-xs font-medium transition-colors border border-border"
            onClick={() => setShowLotesContado(false)}
            title="Volver"
          >
            Volver
          </button>
        </div>
        <div className="w-full max-w-md space-y-3">
          {!props.stats.lotesContado || props.stats.lotesContado.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">No hay lotes de contado registrados.</div>
          ) : (
            props.stats.lotesContado.map((lote: any) => (
              <Card key={lote.id} className="bg-card border border-border rounded-xl hover:bg-accent/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-foreground">{lote.nombre}</h3>
                    <Badge variant="secondary" className="bg-green-500/10 text-green-500 border-green-500/20">
                      Contado
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p><span className="font-medium text-foreground">Precio:</span> Q. {lote.precio?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || 'N/A'}</p>
                    <p><span className="font-medium text-foreground">Área:</span> {lote.area || 'N/A'} m²</p>
                    <p><span className="font-medium text-foreground">Estado:</span> {lote.estado || 'N/A'}</p>
                    {lote.fechaVenta && (
                      <p><span className="font-medium text-foreground">Fecha de Venta:</span> {new Date(lote.fechaVenta).toLocaleDateString('es-GT')}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  if (showInfoFinancieraScreen) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md flex justify-between items-center mb-8">
          <h2 className="text-xl font-semibold text-left text-foreground">Información Financiera</h2>
          <button
            className="px-3 py-1.5 rounded-md bg-secondary hover:bg-accent text-muted-foreground hover:text-foreground text-xs font-medium transition-colors border border-border"
            onClick={() => setShowInfoFinancieraScreen(false)}
            title="Volver"
          >
            Volver
          </button>
        </div>
        <div className="w-full max-w-md space-y-4">
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Ingresos</CardTitle>
              <DollarSign className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">Q. {props.financial.totalIngresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground mt-1">Total de ingresos registrados</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Egresos</CardTitle>
              <DollarSign className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">Q. {props.financial.totalEgresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground mt-1">Total de egresos registrados</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Utilidad Bruta</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">Q. {props.financial.utilidadBruta.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground mt-1">Ingresos menos egresos</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Proyección Anual</CardTitle>
              <BarChart3 className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">Q. {props.financial.proyeccionAnual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground mt-1">Suma de ventas a la fecha</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Promedio Mensual</CardTitle>
              <BarChart3 className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">Q. {props.financial.promedioMensual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground mt-1">Promedio de ingresos por mes</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Mejor Mes</CardTitle>
              <CalendarIcon className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{props.financial.mejorMes}</div>
              <p className="text-xs text-muted-foreground mt-1">Mes con mayor ingreso</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-border rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Eficiencia de Cobranza</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{props.financial.eficiencia.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">% cobrado respecto a proyección</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <SocioDashboardContent {...props} showLotesCredito={showLotesCredito} setShowLotesCredito={setShowLotesCredito} showLotesContado={showLotesContado} setShowLotesContado={setShowLotesContado} search={search} setSearch={setSearch} showInfoFinancieraScreen={showInfoFinancieraScreen} setShowInfoFinancieraScreen={setShowInfoFinancieraScreen} />
  );
}

function SocioDashboardContent({ totalLotes, stats, financial, showLotesCredito, setShowLotesCredito, showLotesContado, setShowLotesContado, search, setSearch, showInfoFinancieraScreen, setShowInfoFinancieraScreen }: any) {
  const { lots, config } = useAppData();
  const [loteSeleccionado, setLoteSeleccionado] = React.useState<string | null>(null);
  // Filtrar lotes a crédito (individuales y grupales, sin duplicados)
  const lotesCredito = React.useMemo(() => {
    const lotes = lots.filter((lote: any) => lote.status === 'crédito' && lote.paymentType === 'pagos');
    // Excluir individuales que estén agrupados
    const lotesIndividualesAExcluir = new Set<string>();
    lotes.forEach((lote: any) => {
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) {
        lote.idLotes.forEach((id: string) => lotesIndividualesAExcluir.add(id));
      }
    });
    return lotes.filter((lote: any) => {
      if (Array.isArray(lote.idLotes) && lote.idLotes.length > 1) return true;
      return !lotesIndividualesAExcluir.has(lote.id || lote.numero);
    });
  }, [lots]);

  // Filtrar lotes de contado
  const lotesContado = React.useMemo(() => {
    return lots.filter((lote: any) => lote.status === 'vendido' && lote.paymentType === 'contado');
  }, [lots]);

  // Filtrar por búsqueda
  const lotesFiltrados = React.useMemo(() => {
    if (!search) return lotesCredito;
    const s = search.toLowerCase();
    return lotesCredito.filter((lote: any) => {
      const nombre = (lote.buyerName || lote.comprador || lote.buyerDPI || "").toLowerCase();
      const numero = (lote.numero || lote.id || "").toLowerCase();
      return nombre.includes(s) || numero.includes(s);
    });
  }, [lotesCredito, search]);

  const { cuotasData, loading } = useCuotasLote(lotesFiltrados);

  // Calcular avance de ventas
  const totalLotesProyecto = config?.lotes || lots.length;
  const lotesVendidos = lots.filter((l: any) => l.status === 'vendido').length;
  const porcentajeVendidos = totalLotesProyecto > 0 ? (lotesVendidos / totalLotesProyecto) * 100 : 0;

  if (showLotesCredito) {
    // Si hay un lote seleccionado, mostrar sus cuotas
    if (loteSeleccionado) {
      const loteData = cuotasData.find((l: any) => l.numero === loteSeleccionado);
      return (
        <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
          <div className="w-full max-w-md flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold text-left text-foreground">Lote {loteSeleccionado}</h2>
            <button
              className="px-3 py-1.5 rounded-md bg-secondary hover:bg-accent text-muted-foreground hover:text-foreground text-xs font-medium transition-colors border border-border"
              onClick={() => setLoteSeleccionado(null)}
            >
              Volver a lotes
            </button>
          </div>
          {loteData ? (
            <CuotasPagosCard loteData={loteData} />
          ) : (
            <div className="text-center text-muted-foreground py-12">No se encontró información del lote.</div>
          )}
        </div>
      );
    }
    // Si no hay lote seleccionado, mostrar listado de lotes
    return (
      <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold text-left text-foreground">Lotes a Crédito</h2>
          <button
            className="px-3 py-1.5 rounded-md bg-secondary hover:bg-accent text-muted-foreground hover:text-foreground text-xs font-medium transition-colors border border-border"
            onClick={() => setShowLotesCredito(false)}
          >
            Volver
          </button>
        </div>
        <div className="max-w-md w-full mb-4">
          <label className="block text-sm font-medium text-muted-foreground mb-1">Buscar por lote o cliente</label>
          <input
            type="text"
            placeholder="Ej: 123, Juan Pérez..."
            className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-1 focus:ring-ring bg-secondary text-foreground placeholder:text-muted-foreground"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full max-w-md space-y-4">
          {loading ? (
            <div className="text-center text-muted-foreground py-12">Cargando lotes a crédito...</div>
          ) : lotesFiltrados.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">No hay lotes a crédito registrados.</div>
          ) : (
            lotesFiltrados.map((lote: any) => (
              <Card
                key={lote.numero || lote.id}
                className="bg-card border border-border rounded-xl cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => setLoteSeleccionado(lote.numero || lote.id)}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                  <CardTitle className="text-lg font-medium text-foreground">Ver Cuotas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">Lote {lote.numero || lote.id}</div>
                  <p className="text-xs text-muted-foreground mt-1">{lote.buyerName || lote.comprador || lote.buyerDPI || "Sin comprador"}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  if (showLotesContado) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md flex justify-between items-center mb-8">
          <h2 className="text-xl font-semibold text-left text-foreground">Lotes de Contado</h2>
          <button
            className="px-3 py-1.5 rounded-md bg-secondary hover:bg-accent text-muted-foreground hover:text-foreground text-xs font-medium transition-colors border border-border"
            onClick={() => setShowLotesContado(false)}
            title="Volver"
          >
            Volver
          </button>
        </div>
        <div className="w-full max-w-md space-y-3">
          {lotesContado.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">No hay lotes de contado registrados.</div>
          ) : (
            lotesContado.map((lote: any) => (
              <Card key={lote.id || lote.numero} className="bg-card border border-border rounded-xl hover:bg-accent/50 transition-colors">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                  <CardTitle className="text-lg font-medium text-foreground">Contado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">Lote {lote.numero || lote.id}</div>
                  <p className="text-xs text-muted-foreground mt-1">{lote.buyerName || lote.comprador || lote.buyerDPI || 'N/A'}</p>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <p><span className="font-medium text-foreground">Precio:</span> Q. {lote.precio?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || 'N/A'}</p>
                    <p><span className="font-medium text-foreground">Área:</span> {lote.area || 'N/A'} m²</p>
                    {lote.fechaVenta && (
                      <p><span className="font-medium text-foreground">Venta:</span> {new Date(lote.fechaVenta).toLocaleDateString('es-GT')}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-6">
      <div className="w-full max-w-md flex justify-between items-center mb-8">
        <h2 className="text-xl font-semibold text-left text-foreground">Panel del Socio</h2>
        <button
          className="px-3 py-1.5 rounded-md bg-secondary hover:bg-red-500/10 hover:text-red-500 text-muted-foreground text-xs font-medium transition-colors border border-border"
          onClick={() => {
            localStorage.removeItem('bimobiUser');
            window.location.reload();
          }}
          title="Cerrar sesión"
        >
          Cerrar sesión
        </button>
      </div>

      <div className="w-full max-w-md space-y-4 mb-8">
        {/* Tarjeta de Lotes a Crédito */}
        <Card className="bg-card border border-border rounded-xl cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowLotesCredito(true)}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Lotes a Crédito</CardTitle>
            <CreditCard className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{stats.credito}</div>
            <p className="text-xs text-muted-foreground mt-1">En proceso de pago</p>
          </CardContent>
        </Card>
        {/* Tarjeta de Lotes de Contado */}
        <Card className="bg-card border border-border rounded-xl cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowLotesContado(true)}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Lotes de Contado</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">{stats.vendidos}</div>
            <p className="text-xs text-muted-foreground mt-1">Pagados completamente</p>
          </CardContent>
        </Card>
        {/* Tarjeta de Información Financiera */}
        <Card className="bg-card border border-border rounded-xl cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setShowInfoFinancieraScreen(true)}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Info. Financiera</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">Información Financiera</div>
            <p className="text-xs text-muted-foreground mt-1">Consulta el estado financiero del proyecto</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 